import React, { useState } from 'react';
import { gql, useQuery, useMutation } from '@apollo/client';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatDateTime } from '../utils/helpers';
import './Events.css';

const GET_EVENTS = gql`
  query GetEvents($filter: EventFilterInput) {
    droneEvents(filter: $filter) {
      id droneId model frequencyMhz signalDb directionDeg
      altitudeM distanceM speedMs lat lng sensorType
      reliability isFalseAlarm detectedAt
    }
    droneEventCount(filter: $filter)
  }
`;

const GET_MY_SITE_REPORT = gql`query { mySite { id name } }`;

const MARK_FALSE_ALARM = gql`
  mutation MarkFalseAlarm($eventId: ID!, $isFalseAlarm: Boolean!) {
    markFalseAlarm(eventId: $eventId, isFalseAlarm: $isFalseAlarm) { id isFalseAlarm }
  }
`;

const EXPORT_EVENTS = gql`
  mutation ExportEvents($filter: EventFilterInput) { exportEvents(filter: $filter) }
`;

const CLEAR_EVENTS = gql`mutation { clearEvents }`;

export default function Events() {
  const [period, setPeriod] = useState<'today'|'7days'|'all'>('7days');
  const [search, setSearch] = useState('');

  const now = new Date();
  const from = period === 'today'
    ? new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString()
    : period === '7days'
      ? new Date(now.getTime() - 7*24*3600*1000).toISOString()
      : undefined;

  const { data, loading, refetch } = useQuery(GET_EVENTS, {
    variables: { filter: { from, limit: 200 } },
    pollInterval: 5000,
  });
  const siteQ = useQuery(GET_MY_SITE_REPORT);

  const [markFalseAlarm] = useMutation(MARK_FALSE_ALARM);
  const [exportEvents] = useMutation(EXPORT_EVENTS);
  const [clearEvents] = useMutation(CLEAR_EVENTS);

  const events = (data?.droneEvents || []).filter((e: any) =>
    !search || e.model?.toLowerCase().includes(search.toLowerCase()) || e.droneId.includes(search)
  );

  const handleExport = async () => {
    const { data } = await exportEvents({ variables: { filter: { from } } });
    const blob = new Blob([data.exportEvents], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `drone-events-${Date.now()}.csv`; a.click();
  };

  const handleClear = async () => {
    if (!window.confirm('Clear all events?')) return;
    await clearEvents();
    refetch();
  };

  const periodLabel = period === 'today' ? 'Today' : period === '7days' ? 'Last 7 days' : 'All time';

  const handlePdf = () => {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    const W = doc.internal.pageSize.getWidth();
    const siteName = siteQ.data?.mySite?.name || 'Object';
    const now = new Date();

    // Header bar
    doc.setFillColor(10, 22, 40);
    doc.rect(0, 0, W, 64, 'F');
    doc.setTextColor(79, 195, 247);
    doc.setFont('helvetica', 'bold'); doc.setFontSize(18);
    doc.text('DroneDefender', 40, 30);
    doc.setTextColor(220, 235, 245);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(12);
    doc.text('Incident Report', 40, 48);
    doc.setFontSize(9); doc.setTextColor(150, 170, 185);
    doc.text('Generated: ' + now.toISOString(), W - 40, 30, { align: 'right' });
    doc.text('CONFIDENTIAL', W - 40, 44, { align: 'right' });

    // Meta
    doc.setTextColor(40, 40, 40); doc.setFontSize(11);
    doc.text('Object: ' + siteName, 40, 88);
    doc.text('Period: ' + periodLabel, 40, 104);

    const confirmed = events.filter((e: any) => !e.isFalseAlarm).length;
    const falseN = events.length - confirmed;
    doc.setFontSize(10); doc.setTextColor(90, 90, 90);
    doc.text(
      'Total incidents: ' + events.length + '    Confirmed: ' + confirmed + '    Flagged false alarm: ' + falseN,
      40, 122
    );

    // Top models
    const counts: Record<string, number> = {};
    events.forEach((e: any) => { const m = e.model || 'Unknown'; counts[m] = (counts[m] || 0) + 1; });
    const top = Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 5)
      .map(([m, c]) => m + ' (' + c + ')').join(', ');
    if (top) doc.text('Top models: ' + top, 40, 138);

    autoTable(doc, {
      startY: 150,
      head: [['Time (UTC)', 'Target ID', 'Model', 'Sensor', 'Alt (m)', 'Speed (m/s)', 'Position', 'Reliability', 'Status']],
      body: events.map((e: any) => [
        new Date(e.detectedAt).toISOString().replace('T', ' ').slice(0, 19),
        e.droneId,
        e.model || '—',
        e.sensorType || '—',
        e.altitudeM != null ? e.altitudeM.toFixed(0) : '—',
        e.speedMs != null ? e.speedMs.toFixed(1) : '—',
        e.lat != null ? e.lat.toFixed(4) + ', ' + (e.lng != null ? e.lng.toFixed(4) : '?') : '—',
        ((e.reliability || 1) * 100).toFixed(0) + '%',
        e.isFalseAlarm ? 'False alarm' : 'Confirmed',
      ]),
      styles: { fontSize: 8, cellPadding: 3 },
      headStyles: { fillColor: [2, 136, 209], textColor: 255 },
      alternateRowStyles: { fillColor: [240, 247, 252] },
      margin: { left: 40, right: 40 },
    });

    // Footer page numbers
    const pages = doc.getNumberOfPages();
    for (let i = 1; i <= pages; i++) {
      doc.setPage(i);
      doc.setFontSize(8); doc.setTextColor(150, 150, 150);
      doc.text('DroneDefender — ' + siteName + ' — ' + periodLabel, 40, doc.internal.pageSize.getHeight() - 16);
      doc.text('Page ' + i + ' / ' + pages, W - 40, doc.internal.pageSize.getHeight() - 16, { align: 'right' });
    }

    const stamp = now.toISOString().slice(0, 10);
    doc.save('DroneDefender-incident-report-' + stamp + '.pdf');
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="page-title">
          <h2>📋 Drone Events</h2>
          <span className="event-count">{data?.droneEventCount ?? 0} total</span>
        </div>
        <div className="page-actions">
          <div className="period-tabs">
            {(['today','7days','all'] as const).map(p => (
              <button key={p} className={`period-tab ${period===p?'active':''}`} onClick={() => setPeriod(p)}>
                {p === 'today' ? 'Today' : p === '7days' ? 'Last 7 Days' : 'All Time'}
              </button>
            ))}
          </div>
          <input className="search-input" placeholder="Search model / ID..." value={search} onChange={e => setSearch(e.target.value)} />
          <button className="btn-secondary" onClick={handleExport}>⬇️ Export CSV</button>
          <button className="btn-secondary" onClick={handlePdf}>📄 PDF Report</button>
          <button className="btn-danger" onClick={handleClear}>🗑️ Clear</button>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading events...</div>
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Time</th><th>Model</th><th>ID</th><th>Freq (MHz)</th>
                <th>Signal</th><th>Dir</th><th>Alt</th><th>Speed</th>
                <th>Lat/Lng</th><th>Sensor</th><th>Reliability</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {events.map((e: any) => (
                <tr key={e.id} className={e.isFalseAlarm ? 'row-false-alarm' : ''}>
                  <td className="td-time">{formatDateTime(e.detectedAt)}</td>
                  <td><span className="model-tag">{e.model || '—'}</span></td>
                  <td><code className="id-code">{e.droneId}</code></td>
                  <td>{e.frequencyMhz ?? '—'}</td>
                  <td>{e.signalDb?.toFixed(1) ?? '—'} dB</td>
                  <td>{e.directionDeg?.toFixed(0) ?? '—'}°</td>
                  <td>{e.altitudeM?.toFixed(0) ?? '—'}m</td>
                  <td>{e.speedMs?.toFixed(1) ?? '—'} m/s</td>
                  <td>{e.lat ? `${e.lat.toFixed(4)}, ${e.lng?.toFixed(4)}` : '—'}</td>
                  <td><span className="sensor-chip">{e.sensorType}</span></td>
                  <td>{((e.reliability||1)*100).toFixed(0)}%</td>
                  <td>
                    <button
                      className={`btn-xs ${e.isFalseAlarm ? 'active' : ''}`}
                      onClick={() => markFalseAlarm({ variables: { eventId: e.id, isFalseAlarm: !e.isFalseAlarm } })}
                    >
                      {e.isFalseAlarm ? '✓ False' : 'False?'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {events.length === 0 && <div className="no-data">No events found</div>}
        </div>
      )}
    </div>
  );
}
